import { defineQuiz } from "./_schema";
import type { Answers, PersonaKey, Question } from "./_schema";
import { deriveEnergyProfile } from "./quiz-energy-profile";

/**
 * ENERGY QUIZ -- cold-traffic destination for the low-energy ad set
 * ("Is it just getting older, or is something draining your energy?").
 *
 * The quiz is the ad unpacked: each panel = one question, in the ad's own
 * order, so cold traffic lands on the ad it clicked. Persuasion spine is the
 * two operator-authored bridges: absolution mid-flow ("it's not just aging")
 * -> named-villain payoff after the last question ("low testosterone could be
 * the reason you're running on E"). The verdict is the locked T-suppression
 * surface, routed ORGANICALLY like Ignite -- no forced cause (see
 * quizzes/quiz-energy-profile.ts). The angle's villain IS low testosterone,
 * which is the whole verdict, not a narrower root-cause key.
 *
 * 10 single-choice screens + 3 interstitials (2 bridges + analyzing) = 13.
 * Design spec: docs/specs/2026-07-16-quiz-energy-quiz-design.md.
 *
 * Like ignite, cortisol, and weight-loss, this quiz has no GLP-1 screener, so
 * `glp1-user` is not reachable from the classifier.
 */

const QUESTION_ORDER = [
  "age",
  "energy-pattern",
  "energy-goal",
  "energy-3pm",
  "motivation-drop",
  "info-absolution",
  "sleep-quality",
  "libido",
  "body-change",
  "focus-mood",
  "when-started",
  "info-mechanism",
  "info-analyzing",
] as const;

const questions: Question[] = [
  {
    id: "age",
    type: "single",
    prompt: "How old are you?",
    // Reused ignite id -- option values are the deriver contract.
    options: [
      { value: "50-plus", label: "50+ years old" },
      { value: "40-49", label: "40-49 years old" },
      { value: "30-39", label: "30-39 years old" },
      { value: "20-29", label: "20-29 years old" },
    ],
  },
  {
    id: "energy-pattern",
    type: "single",
    prompt: "How would you describe your energy on a typical day?",
    options: [
      { value: "wired-then-wiped", label: "Wired in the morning, wiped out by afternoon" },
      { value: "tired-on-waking", label: "Tired the moment I wake up, no matter how I sleep" },
      { value: "up-and-down", label: "Up and down, never steady" },
      { value: "all-of-above", label: "All of the above" },
    ],
  },
  {
    id: "energy-goal",
    type: "single",
    // Positive beat -- the flow's one aspiration screen before the negative run.
    prompt: "When you picture having your energy back, what do you want most?",
    options: [
      { value: "stop-dragging", label: "To stop dragging through the afternoon" },
      { value: "enjoy-life", label: "To have the energy to enjoy life again" },
      { value: "feel-myself", label: "All of it, I want to feel like myself again" },
    ],
  },
  {
    id: "energy-3pm",
    type: "single",
    prompt: "Do you hit a wall in the afternoon that caffeine doesn't fix?",
    // Reused ignite id -- option values are the deriver contract.
    options: [
      { value: "drained", label: "Yes, almost every day around 2 or 3pm" },
      { value: "afternoon-slump", label: "Yes, a few times a week" },
      { value: "slightly-tired", label: "Occasionally" },
      { value: "same-energy", label: "No" },
    ],
  },
  {
    id: "motivation-drop",
    type: "single",
    prompt: "Has your motivation or drive dropped off in the last year or two?",
    options: [
      { value: "significantly", label: "Yes, I don't chase the things I want anymore" },
      { value: "somewhat", label: "Somewhat, the spark isn't what it was" },
      { value: "not-sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "info-absolution",
    type: "info",
    heading: "It's not \"just aging,\" and it's not in your head.",
    body: [
      "You're doing the things you're supposed to. You show up, you push through, you get the sleep you can.",
      "But feeling this drained day after day isn't something you're supposed to just accept. When energy falls off this steadily, there's usually a reason underneath it, and it's one many guys overlook.",
      "A few more questions and we'll show you what's likely driving it.",
    ],
    // Bridge 1 -- absolution. Mechanism-silent: names no hormone, keeps the loop
    // open for Bridge 2 ("a reason underneath it"). Image renders below the
    // Continue CTA (rule 20) as supporting proof.
    image: {
      src: "/static/images/quiz-energy-absolution.webp",
      alt: "A four-panel cartoon of a tired man in a blue shirt: head in hands at his desk over a to-do list, yawning through a 5pm meeting, slumped at his desk late at night beside a caffeine mug, and trudging home down a city street with his briefcase -- drained no matter what he does.",
    },
  },
  {
    id: "sleep-quality",
    type: "single",
    prompt: "How's your sleep, really?",
    options: [
      { value: "wake-tired", label: "I sleep ok but wake up just as tired" },
      { value: "wake-through-night", label: "I wake up through the night" },
      { value: "cant-sleep", label: "I can't fall asleep, then can't get up" },
      { value: "sleep-fine-still-tired", label: "Sleep is fine, the tiredness is still there" },
    ],
  },
  {
    id: "libido",
    type: "single",
    prompt: "Has your sex drive or interest dropped compared to a few years ago?",
    options: [
      { value: "significantly", label: "Significantly" },
      { value: "somewhat", label: "Somewhat" },
      { value: "not-sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Testosterone drives libido and energy from the same tank. When drive fades first, it's often the earliest signal to check on your T.",
    },
  },
  {
    id: "body-change",
    type: "single",
    prompt: "Have you noticed changes in your body, even with the same routine?",
    options: [
      { value: "more-belly", label: "More belly fat that won't budge" },
      { value: "softer-muscle", label: "Muscle feels softer or harder to hold onto" },
      { value: "both", label: "Both" },
      { value: "no-change", label: "No real change" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Low testosterone makes it easier to store fat and harder to keep muscle. That shift often shows up on the body before you connect it to how flat you feel.",
    },
  },
  {
    id: "focus-mood",
    type: "single",
    prompt: "How's your focus and mood?",
    options: [
      { value: "foggy", label: "Foggy, forgetful, and harder to lock in" },
      { value: "flat-irritable", label: "I feel flatter or more irritable than I used to be" },
      { value: "both", label: "Both" },
      { value: "no-change", label: "No change" },
    ],
  },
  {
    id: "when-started",
    type: "single",
    prompt: "How long have you been noticing these changes?",
    // Reused ignite id -- option values are the deriver contract.
    options: [
      { value: "over-year", label: "Over a year ago" },
      { value: "past-year", label: "In the past year" },
      { value: "few-months", label: "In the past few months" },
      { value: "not-sure", label: "Not sure" },
    ],
  },
  {
    id: "info-mechanism",
    type: "info",
    heading: "Low testosterone could be the reason you're running on E.",
    body: [
      "Testosterone doesn't just fuel your sex drive. It's tied to your energy, your focus, your mood, and how your body holds muscle.",
      "When it drifts down, which it does for most men as they age, the first things to go are the things you just described. The afternoon wall. The missing drive. The fog. The softer body even though you're doing the same workouts.",
      "Most men chalk it up to age and push through. But the tiredness usually isn't the age. It could be what's happening underneath. Let's see how many of the signs point that way.",
    ],
    // Bridge 2 -- named-villain payoff. First naming of "testosterone" -- the
    // whole T-suppression verdict is the reveal. Image renders below the
    // Continue CTA (rule 20): the "testosterone declines with age" education
    // card (deliberate UMS placement, first product-adjacent visual).
    image: {
      src: "/static/images/quiz-energy-mechanism.webp",
      alt: "A dark education card titled 'Testosterone declines with age' showing a row of male silhouettes fading from bright orange (peak T in your 20s) to dark (declining T at 40+), above three labeled tiles: low energy, brain fog, and softer body.",
    },
  },
  {
    id: "info-analyzing",
    type: "info",
    // Pays off Bridge 2's closing line ("Let's see how many of the signs point
    // that way").
    heading: "Analyzing your answers…",
    body: [
      "Counting the signs you just reported, and measuring how far your energy has drifted.",
    ],
    animation: "analyzing",
    autoAdvanceMs: 5000,
  },
];

export const quizEnergyQuiz = defineQuiz({
  id: "quiz-energy",
  slug: "quiz-energy",
  meta: {
    title: "What's Really Draining Your Energy",
    introHeadline: "Discover What's Secretly Draining Your Energy in Under 2 Minutes",
    introAccent: "draining your energy",
    introBody:
      "See what's really behind the afternoon crashes, the flat mood, and the drive that isn't there anymore, in this 2 minute quiz.",
    testimonial: {
      // Real verified review (operator-confirmed). Angle-matched (energy/tired/
      // aging register), reveal-safe (names no product or T category), says
      // "quiz" not bare "it". First name varied off the live weight-loss quiz's
      // "DAVID R." so the same name isn't on two live quizzes (operator
      // directive 2026-07-16); set to the real reviewer's initials if supplied.
      quote:
        "I thought I was just tired from work and getting older. Turns out there was an actual reason my energy fell off a cliff. This quiz laid it out in about two minutes. Wish I'd taken it a year ago.",
      author: "MICHAEL T., Verified Review",
    },
  },
  questions,

  branching(_answers: Answers, currentQuestionId: string): string | null {
    const idx = QUESTION_ORDER.indexOf(currentQuestionId as (typeof QUESTION_ORDER)[number]);
    if (idx < 0) return null;
    const next = QUESTION_ORDER[idx + 1];
    return next ?? null;
  },

  classifier(answers: Answers): PersonaKey {
    const age = answers["age"];
    const energy = answers["energy-3pm"];
    const onset = answers["when-started"];
    const libido = answers["libido"];
    const body = answers["body-change"];
    const sleep = answers["sleep-quality"];

    // Note: energy-pattern, motivation-drop, energy-goal, and focus-mood are not
    // read here -- they are persuasion / severity screens that feed the deriver
    // via quizzes/quiz-energy-profile.ts. The classifier routes on age, energy,
    // onset, libido, body-change, and sleep-quality only.

    // Performance-focused: minimal-symptom optimizer. Runs first -- a
    // no-symptoms profile never falls through to a decline persona.
    if (body === "no-change" && (energy === "same-energy" || energy === "slightly-tired") && libido === "no") {
      return "performance";
    }

    // TRT-alternative: long-running collapse across energy + drive/age.
    if (onset === "over-year" && energy === "drained" && (libido === "significantly" || age === "50-plus")) {
      return "trt-alternative";
    }

    // Skeptical buyer: can't place when it started.
    if (onset === "not-sure") {
      return "skeptical-buyer";
    }

    // Aging men: 40+ with visible body change or disrupted sleep.
    if (
      (age === "50-plus" || age === "40-49") &&
      (body === "more-belly" ||
        body === "both" ||
        body === "softer-muscle" ||
        sleep === "cant-sleep" ||
        sleep === "wake-through-night")
    ) {
      return "aging-men";
    }

    return "everyday-man";
  },

  deriveProfile: deriveEnergyProfile,
});
